import React from 'react'
import { Filters, FiltersRow } from './filters'
import { FilteredContacts } from './contacts'
import { InboxTab } from './inboxes'
import { capitalize } from '../../utils'
import { Message, MessageData } from './message'
import { Loader } from '../../components/simple/loader'
import { Button, ButtonStyle } from '../../components/simple/button'
import { makeRequest, showAxiosErrorAsToast, usePaginatedData } from '../../request'
import { useDebounce } from '../../hooks/hooks'
import { User } from '../../providers/context'
import {
  IconArchive,
  IconInbox,
  IconMinusSquare,
  IconPlusSquare,
  IconReadSolid,
  IconSquare,
  IconTimes,
  IconUnreadSolid,
} from '../../icons/library'

interface MessagesProps {
  user: User
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  filteredContacts: FilteredContacts
  tab: { tab: InboxTab }
  messageSelected: (message: MessageData) => void
  selectedMessageID: number | undefined
  reloadUnreadCounts: () => void
  showFiltersOnMobile: boolean
}

export const Messages: React.FC<MessagesProps> = React.memo(
  ({
    user,
    filters,
    setFilters,
    filteredContacts,
    tab,
    messageSelected,
    selectedMessageID,
    reloadUnreadCounts,
    showFiltersOnMobile,
  }) => {
    const messagePostData = React.useMemo(
      () => ({
        filters: {
          ...filters,
          searchUserIDs: filters.searchUsers.map((u) => u.id),
          searchUsers: undefined,
        },
        filteredContacts,
        tab: tab.tab,
      }),
      [filters, filteredContacts, tab]
    )
    const {
      values: messages,
      loadingMore,
      loading: messagesLoading,
      loadMore,
      reload: reloadMessages,
      stale,
    } = usePaginatedData<MessageData, typeof messagePostData>(
      '/frontend_api/getMessages',
      messagePostData,
      () => !filters.allSchoolMessages && reloadUnreadCounts()
    )

    const reloadDebounced = useDebounce(reloadMessages, 400)

    const [enableMessageMultiSelectRaw, setEnableMessageMultiSelect] = React.useState(false)
    const enableMessageMultiSelect = !filters.allSchoolMessages && enableMessageMultiSelectRaw
    const [multiSelectedMessages, setMultiSelectedMessages] = React.useState<Set<number>>(new Set())
    const [lastSelectedMessageId, setLastSelectedMessageId] = React.useState<number | undefined>()
    React.useEffect(() => {
      // ensure all selected messages are still in the list
      setMultiSelectedMessages((prev) => {
        if (filters.allSchoolMessages) return new Set()
        return new Set([...prev].filter((m) => messages.find((msg) => msg.id === m)))
      })
    }, [messages, filters.allSchoolMessages])
    const setMultiSelectedHelper = (checked: boolean, shiftPressed: boolean, message: MessageData) => {
      setLastSelectedMessageId(message.id)
      let messagesToToggle = [message]
      const lastSelectedMessage = messages.find((m) => m.id === lastSelectedMessageId)
      if (shiftPressed && lastSelectedMessage) {
        const startIndex = messages.indexOf(lastSelectedMessage)
        const endIndex = messages.indexOf(message)
        const [start, end] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
        messagesToToggle = messages.slice(start, end + 1)
      }
      setMultiSelectedMessages((prev) => {
        const newSet = new Set(prev)
        messagesToToggle.forEach((msg) => {
          if (!checked) newSet.delete(msg.id)
          else newSet.add(msg.id)
        })
        return newSet
      })
    }

    const canArchive =
      (tab.tab === InboxTab.Inbox || tab.tab === InboxTab.Archived || tab.tab === InboxTab.Sent) &&
      !filters.allSchoolMessages

    const [overrideReadMessages, setOverrideReadMessages] = React.useState<Record<number, boolean>>({})
    React.useEffect(() => {
      const newOverrideReadMessages: Record<number, boolean> = {}
      if (!stale) for (const message of messages) newOverrideReadMessages[message.id] = overrideReadMessages[message.id]
      setOverrideReadMessages(newOverrideReadMessages)
    }, [messages])

    const markAllRead = (messageIDs: Set<number>, read: boolean) => {
      makeRequest('/frontend_api/markAllRead', {
        read,
        messageIDs,
      })
        .then(() => {
          setOverrideReadMessages((prev) => {
            const newOverrideReadMessages = { ...prev }
            for (const messageID of messageIDs) {
              newOverrideReadMessages[messageID] = read
            }
            return newOverrideReadMessages
          })
          reloadUnreadCounts()
        })
        .catch(showAxiosErrorAsToast)
    }

    const markAllArchived = (messageIDs: Set<number>, archived: boolean) => {
      makeRequest('/frontend_api/markAllArchived', {
        archived,
        messageIDs,
      })
        .then(() => reloadDebounced())
        .catch(showAxiosErrorAsToast)
    }

    return (
      <>
        <FiltersRow
          filters={filters}
          setFilters={setFilters}
          user={user}
          showOnMobile={showFiltersOnMobile}
          before={
            <>
              {enableMessageMultiSelect ? (
                <>
                  <Button
                    buttonStyle={ButtonStyle.ghostLight}
                    className="!px-2"
                    tooltip={multiSelectedMessages.size > 0 ? 'Deselect All' : 'Select All'}
                    onClick={() =>
                      multiSelectedMessages.size > 0
                        ? setMultiSelectedMessages(new Set())
                        : setMultiSelectedMessages(new Set(messages.filter((m) => !m.notYourMessage).map((m) => m.id)))
                    }
                  >
                    {multiSelectedMessages.size > 0 ? <IconMinusSquare size="lg" /> : <IconPlusSquare size="lg" />}
                  </Button>
                  {multiSelectedMessages.size > 0 && (
                    <div className="flex items-center gap-3 rounded bg-slate-50 px-3 py-2 text-sm text-slate-600 shadow">
                      <b>{multiSelectedMessages.size}</b>
                      {messages
                        .filter((m) => multiSelectedMessages.has(m.id))
                        .find((m) => overrideReadMessages[m.id] ?? m.read) ? (
                        <IconUnreadSolid
                          className="cursor-pointer"
                          size="lg"
                          onClick={() => markAllRead(multiSelectedMessages, false)}
                          tooltip="Unread selected"
                        />
                      ) : (
                        <IconReadSolid
                          className="mb-0.5 cursor-pointer"
                          size="lg"
                          onClick={() => markAllRead(multiSelectedMessages, true)}
                          tooltip="Read selected"
                        />
                      )}
                      {canArchive &&
                        (tab.tab === InboxTab.Archived ? (
                          <IconInbox
                            className="cursor-pointer"
                            size="lg"
                            tooltip="Unarchive selected"
                            onClick={() => markAllArchived(multiSelectedMessages, false)}
                          />
                        ) : (
                          <IconArchive
                            className="cursor-pointer"
                            size="lg"
                            tooltip="Archive selected"
                            onClick={() => markAllArchived(multiSelectedMessages, true)}
                          />
                        ))}
                    </div>
                  )}
                  <Button
                    buttonStyle={ButtonStyle.ghostLight}
                    className="!px-2"
                    onClick={() => {
                      setEnableMessageMultiSelect(false)
                      setMultiSelectedMessages(new Set())
                    }}
                  >
                    <IconTimes className="text-gray-300" />
                  </Button>
                </>
              ) : (
                <Button
                  buttonStyle={ButtonStyle.ghostLight}
                  className="!px-2"
                  onClick={() => setEnableMessageMultiSelect(true)}
                  tooltip="Select messages"
                  disabled={filters.allSchoolMessages}
                >
                  <IconSquare className="text-slate-300" size="lg" />
                </Button>
              )}
              <div className="my-2 w-px self-stretch bg-gray-200" />
            </>
          }
        />
        <div className="flex-grow overflow-y-auto overflow-x-hidden bg-slate-50 px-[.375rem]">
          {!messages ||
          (stale && stale.current.tab !== stale.old?.tab) ||
          (messagesLoading && messages.length === 0) ? (
            <Loader />
          ) : messages.length > 0 ? (
            <>
              {messages.map((message) => (
                <Message
                  key={message.id}
                  message={message}
                  onSelect={(m) => {
                    if (message.canRead) setOverrideReadMessages((prev) => ({ ...prev, [m.id]: true }))
                    messageSelected(m)
                  }}
                  overrideRead={overrideReadMessages[message.id]}
                  selected={selectedMessageID === message.id}
                  tab={tab.tab}
                  reloadMessages={reloadDebounced}
                  canSwipeToArchive={canArchive && !message.notYourMessage}
                  setMultiSelected={
                    enableMessageMultiSelect && !message.notYourMessage
                      ? (c, s) => setMultiSelectedHelper(c, s, message)
                      : undefined
                  }
                  multiSelected={multiSelectedMessages.has(message.id)}
                  disabled={messagesLoading}
                />
              ))}
              {loadMore ? (
                <Button className="mx-auto my-3" logo small onClick={loadMore} disabled={loadingMore}>
                  Load More
                </Button>
              ) : (
                <Button className="mx-auto my-3 cursor-default opacity-0">Load More</Button>
              )}
            </>
          ) : tab.tab === InboxTab.Archived ? (
            <div className="mt-8 text-center">
              No archived messages found. <br />
              Swipe left on a message to archive it.
            </div>
          ) : (
            <div className="mt-8 text-center">No Results Found in {capitalize(tab.tab)}</div>
          )}
        </div>
      </>
    )
  }
)
